// API client pour les clients
export interface CreateClientData {
  civility: 'M.' | 'Mme' | 'Mlle';
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  address: string;
  wilaya: string;
  city: string;
  notes?: string;
  transaction_type: 'sale' | 'rent' | 'short-rent';
  property_type: string;
  min_budget: number;
  max_budget: number;
  min_area: number;
  min_rooms: number;
  criteria?: Record<string, boolean>;
  interest_level?: 'very-interested' | 'interested' | 'to-contact';
  agent_id?: number;
}

export interface UpdateClientData extends Partial<CreateClientData> {
  status?: 'active' | 'archived';
}

export interface Client {
  id: number;
  agency_id: number;
  created_by_user_id: number;
  civility: 'M.' | 'Mme' | 'Mlle';
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  address: string;
  wilaya: string;
  city: string;
  notes?: string;
  status: 'active' | 'archived';
  transaction_type: 'sale' | 'rent' | 'short-rent';
  property_type: string;
  min_budget: number;
  max_budget: number;
  min_area: number;
  min_rooms: number;
  criteria?: Record<string, boolean>;
  interest_level: 'very-interested' | 'interested' | 'to-contact';
  last_contact: string;
  created_at: string;
  updated_at: string;
  agent_id?: number;
  agent_name?: string;
  // Propriétés calculées pour compatibilité
  firstName: string;
  lastName: string;
  interestLevel: string;
}

class ClientAPI {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/clients`;

  async getAll(filters?: {
    agency_id?: number;
    status?: string;
    transaction_type?: string;
    interest_level?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.agency_id) params.append('agency_id', filters.agency_id.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type);
    if (filters?.interest_level) params.append('interest_level', filters.interest_level);

    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch clients');
    
    const data = await response.json();
    
    // Transformer les données pour compatibilité avec l'interface existante
    return data.map((client: any) => {
      // MySQL2 parse automatiquement les colonnes JSON, donc client.criteria est déjà un objet
      const parsedCriteria = typeof client.criteria === 'string'
        ? JSON.parse(client.criteria)
        : (client.criteria || {});

      return {
        ...client,
        firstName: client.first_name,
        lastName: client.last_name,
        interestLevel: client.interest_level,
        criteria: {
          transactionType: client.transaction_type,
          propertyType: client.property_type,
          minBudget: client.min_budget,
          maxBudget: client.max_budget,
          minArea: client.min_area,
          minRooms: client.min_rooms,
          ...parsedCriteria
        }
      };
    });
  }

  async create(data: CreateClientData, agency_id: number, created_by_user_id: number) {
    const payload = {
      ...data,
      agency_id,
      created_by_user_id
    };

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create client');
    }
    return response.json();
  }

  async update(id: number, data: UpdateClientData, agency_id?: number) {
    const payload = agency_id ? { ...data, agency_id } : data;

    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update client');
    }
    return response.json();
  }

  async delete(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete client');
    }
    return response.json();
  }

  async archive(id: number) {
    return this.update(id, { status: 'archived' });
  }

  async activate(id: number) {
    return this.update(id, { status: 'active' });
  }
}

export const clientAPI = new ClientAPI();